import {ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import * as d3 from 'd3';
import {GrafoAziendeScreenshotData} from '../../../../../../shared/models';
import {TranslateService} from '@ngx-translate/core';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {environment} from '../../../../../../../environments/environment';
import {UtilsService} from '@shared-lib';
import {StorageService} from '../../../../../../core/service';
import {Message} from 'primeng/message';
import {catchError, Observable, of} from 'rxjs';
import {
  adattaSfondoEtichetta,
  calcolaLayoutAlbero,
  costruisciDatiGrafo,
  righeEtichettaArco,
  scriviEtichettaMultiriga
} from '../models/grafo-aziende-layout';

const GRAFO_AZIENDE_DATA = 'GRAFO_AZIENDE_DATA'; // Costante per test in locale

/** la tela finisce come immagine dentro il report: le misure restano quelle attese dal PDF */
const LARGHEZZA_TELA = 800;
const ALTEZZA_TELA = 500;

@Component({
  selector: 'grafo-aziende-screenshot',
  imports: [ProgressSpinnerModule, Message],
  templateUrl: './grafo-aziende-screenshot.component.html',
  standalone: true
})
export class GrafoAziendeScreenshotComponent implements OnInit, OnDestroy {
  @ViewChild('graphContainer', {static: false}) graphContainer!: ElementRef;

  graphData: GrafoAziendeScreenshotData | null = null; // Dati dal postMessage

  // Parametri di configurazione grafici
  nodeRadius: number = 30;
  spaziaturaX: number = 220;
  altezzaRiga: number = 130;

  private svg: any;

  constructor(
    public utils: UtilsService,
    private storage: StorageService,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (environment.ambiente === 'LOCAL') {
      const localData = this.storage.getItemSession(GRAFO_AZIENDE_DATA);
      if (localData) {
        window.postMessage(JSON.parse(localData), '*');
      }
    }

    window.addEventListener('message', event => {
      console.log('window message grafo aziende', event.data);
      this.graphData = event.data;

      if (this.utils.checkValid(this.graphData)) {
        this.cdRef.detectChanges(); // Forza l'aggiornamento per renderizzare il #graphContainer
        this.applicaLingua().subscribe(() => this.initGraphData());
      }
    });

    window['messageListenerOK'] = true;
    document.body.classList.remove('body-bg-color');
  }

  ngOnDestroy() {
    document.body.classList.add('body-bg-color');
  }

  /**
   * Questa pagina viene aperta dal browser headless senza sessione utente, quindi la lingua dell'app
   * cade sul browser (inglese). Le sigle dei diritti vanno tradotte nella lingua scelta per il report,
   * che arriva insieme ai dati: la applichiamo e attendiamo il caricamento del json prima di disegnare.
   */
  private applicaLingua(): Observable<unknown> {
    const lang = this.graphData?.lang?.toLowerCase();
    if (!lang || lang === this.translate.currentLang) {
      return of(null);
    }
    return this.translate.use(lang).pipe(catchError(() => of(null)));
  }

  private initGraphData() {
    if (!this.graphData || !this.graphContainer) return;

    const container = this.graphContainer.nativeElement;

    // Svuota per sicurezza
    d3.select(container).selectAll('*').remove();

    this.svg = d3.select(container).append('svg');
    this.svg.append('g');

    this.drawStaticGraph();
  }

  private drawStaticGraph() {
    const {nodes, links} = costruisciDatiGrafo(
      this.graphData!.aziende,
      this.graphData!.soggettoId,
      this.graphData!.soggettoNome,
      (chiave, parametri) => this.translate.instant(chiave, parametri)
    );

    // Stesso albero del grafo a schermo, alle sue dimensioni naturali. L'immagine per il report resta
    // però 800x500: il viewBox ci mappa sopra l'albero e l'SVG scala tutto in proporzione, invece di
    // stringere solo le distanze e far finire i nodi uno sull'altro.
    const {width, height} = calcolaLayoutAlbero(nodes, links, this.graphData!.soggettoId, {
      larghezzaDisponibile: LARGHEZZA_TELA,
      altezzaMinima: ALTEZZA_TELA,
      spaziaturaX: this.spaziaturaX,
      altezzaRiga: this.altezzaRiga
    });

    const container = this.graphContainer.nativeElement;
    container.style.height = `${ALTEZZA_TELA}px`;
    this.svg
      .attr('width', LARGHEZZA_TELA)
      .attr('height', ALTEZZA_TELA)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const g = this.svg.select('g');
    const NODE_RADIUS = this.nodeRadius;
    const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

    // Costruzione Grafica Sincrona (Nessuna animazione)
    const linkSelection = g
      .append('g')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('fill', 'none')
      .attr('stroke', (d: any) => (d.target.type === 'asset' ? '#888' : '#999'))
      .attr('stroke-width', (d: any) => (d.target.type === 'asset' ? 1.5 : 2))
      .attr('stroke-dasharray', (d: any) => (d.target.type === 'asset' ? '6 4' : null));

    // quote societarie verso le aziende, conteggio degli immobili verso il nodo immobili
    const linkLabels = g
      .append('g')
      .selectAll('g')
      .data(links.filter((d: any) => righeEtichettaArco(d).length))
      .enter()
      .append('g')
      .attr('class', 'link-label-group');

    linkLabels.append('rect').attr('fill', 'white').attr('stroke', '#ddd').attr('stroke-width', 0.5).attr('rx', 3).attr('ry', 3);
    linkLabels
      .append('text')
      .attr('class', 'link-label')
      .attr('text-anchor', 'middle')
      .style('font-size', '11px')
      .style('font-weight', 'bold')
      .style('fill', '#333');

    // un diritto per riga, con lo sfondo dimensionato sul blocco: senza, il testo finirebbe sopra la linea
    linkLabels.each(function (this: SVGGElement, d: any) {
      const testo = this.querySelector('text') as SVGTextElement | null;
      if (testo) {
        scriviEtichettaMultiriga(testo, righeEtichettaArco(d));
      }
      adattaSfondoEtichetta(this);
    });

    const nodeSel = g.append('g').selectAll('g').data(nodes).enter().append('g');

    nodeSel
      .append('foreignObject')
      .attr('width', 64)
      .attr('height', 64)
      .attr('x', -32)
      .attr('y', -32)
      .append('xhtml:div')
      .attr('class', 'node-content')
      .style('width', '64px')
      .style('height', '64px')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .html((d: any) => {
        const isRoot = d.id === this.graphData!.soggettoId ? 'root-node' : '';
        const iconClass = d.type === 'person' ? 'mdi mdi-account' : d.type === 'asset' ? 'mdi mdi-home' : 'mdi mdi-domain';
        return `
      <div class="node-circle ${isRoot}" style="width:60px;height:60px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;border:${d.id === this.graphData!.soggettoId ? '3px solid #0085a4' : '2px solid #ddd'};pointer-events:none;">
        <i class="${iconClass}" style="font-size:32px;color:var(--primary-color);"></i>
      </div>`;
      });

    const nodeLabelG = nodeSel.append('g').attr('transform', 'translate(0, 45)');
    nodeLabelG.append('rect').attr('fill', 'white').attr('stroke', '#ddd').attr('stroke-width', 0.5).attr('rx', 3).attr('ry', 3);
    nodeLabelG
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 4)
      .style('font-size', '11px')
      .style('font-weight', (d: any) => (d.id === this.graphData!.soggettoId ? 'bold' : 'normal'))
      .style('fill', 'var(--text-color)')
      .text((d: any) => d.name);

    nodeLabelG.each(function (this: SVGGElement) {
      adattaSfondoEtichetta(this);
    });

    // posizionamento finale, una volta sola: qui non c'è simulazione da far girare
    nodeSel.attr('transform', (d: any) => {
      d.x = clamp(d.x, NODE_RADIUS, width - NODE_RADIUS);
      d.y = clamp(d.y, NODE_RADIUS, height - NODE_RADIUS);
      return `translate(${d.x}, ${d.y})`;
    });

    linkSelection
      .attr('x1', (d: any) => d.source.x)
      .attr('y1', (d: any) => d.source.y)
      .attr('x2', (d: any) => d.target.x)
      .attr('y2', (d: any) => d.target.y);

    // a metà segmento l'etichetta degli immobili, che è su due righe, finirebbe sopra il nome dell'azienda:
    // la sposto verso il nodo immobili, dove trova spazio libero
    linkLabels.attr('transform', (d: any) => {
      const t = d.target.type === 'asset' ? 0.62 : 0.5;
      return `translate(${d.source.x + (d.target.x - d.source.x) * t}, ${d.source.y + (d.target.y - d.source.y) * t})`;
    });

    // Segnaliamo al sistema che la renderizzazione del canvas SVG è conclusa e pronta allo screen.
    setTimeout(() => {
      window['screenshotOK'] = true;
    }, 100);
  }
}
